import { useState } from 'react';
import { Trash2, Plus, Copy, MoreVertical } from 'lucide-react';
import { ExpandableRow, RowList, type RowTone } from './ui/ListRows';
import { DeliverableRow } from './DeliverableRow';
import { Button } from './ui';
import type { Deliverable, DeliverableStatus, CommissionType } from '../types/multiInfluencer';
import type { Influencer } from '../types/influencer';
import { useLanguage } from '../contexts/LanguageContext';

export interface InfluencerBlockData {
  id: string;
  influencer_id: string | null;
  influencer_name: string;
  agreed_fee: number;
  commission_pct: number;
  commission_flat_fee: number;
  commission_type: CommissionType;
  notes: string;
  deliverables: DeliverableData[];
  existing?: boolean;
  display_order?: number;
}

export interface DeliverableData {
  id: string;
  content: string;
  date: string;
  status: DeliverableStatus;
  fee: number;
  commission: number;
}

interface CampaignInfluencerBlockProps {
  block: InfluencerBlockData;
  availableInfluencers: Influencer[];
  isExpanded: boolean;
  onToggleExpand: () => void;
  onChange: (block: InfluencerBlockData) => void;
  onRemove: () => void;
  onDuplicate: () => void;
  canRemove: boolean;
  removeDisabledReason?: string;
  dragHandleProps?: {
    attributes: Record<string, any>;
    listeners: Record<string, any>;
  };
}

export function CampaignInfluencerBlock({
  block,
  availableInfluencers,
  isExpanded,
  onToggleExpand,
  onChange,
  onRemove,
  onDuplicate,
  canRemove,
  removeDisabledReason,
  dragHandleProps
}: CampaignInfluencerBlockProps) {
  const { t } = useLanguage();
  const [showActions, setShowActions] = useState(false);

  const publishedCount = block.deliverables.filter(d => d.status === 'Published').length;
  const totalCount = block.deliverables.length;

  const handleInfluencerChange = (name: string) => {
    const influencer = availableInfluencers.find(i => i.full_name === name);
    onChange({
      ...block,
      influencer_name: name,
      influencer_id: influencer?.id || null,
      commission_pct: influencer?.default_commission_pct ?? block.commission_pct,
      commission_flat_fee: influencer?.default_commission_flat_fee ?? block.commission_flat_fee,
      commission_type: influencer?.default_commission_type ?? block.commission_type
    });
  };

  const calculateNetPay = (): number => {
    if (block.commission_type === 'flat_fee') {
      return block.agreed_fee - (block.commission_flat_fee || 0);
    }
    return block.agreed_fee * (1 - block.commission_pct);
  };

  const calculateCommissionAmount = (): number => {
    if (block.commission_type === 'flat_fee') {
      return block.commission_flat_fee || 0;
    }
    return block.agreed_fee * block.commission_pct;
  };

  const handleFieldChange = (field: keyof InfluencerBlockData, value: any) => {
    if (field === 'agreed_fee' && block.deliverables.length > 0) {
      const newFee = parseFloat(value) || 0;
      const redistributed = redistributeFees(block.deliverables, newFee);
      onChange({ ...block, [field]: value, deliverables: redistributed });
    } else {
      onChange({ ...block, [field]: value });
    }
  };

  const addDeliverable = () => {
    const newDeliverables = [...block.deliverables, {
      id: `temp-${crypto.randomUUID()}`,
      content: '',
      date: '',
      status: 'Draft' as DeliverableStatus,
      fee: 0,
      commission: 0,
    }];
    const redistributed = redistributeFees(newDeliverables, block.agreed_fee);
    onChange({ ...block, deliverables: redistributed });
  };

  const redistributeFees = (deliverables: DeliverableData[], totalFee: number): DeliverableData[] => {
    if (deliverables.length === 0) return deliverables;
    const perItem = Math.round((totalFee / deliverables.length) * 100) / 100;
    const lastAdjust = Math.round((totalFee - perItem * (deliverables.length - 1)) * 100) / 100;
    const totalCommission = block.commission_type === 'flat_fee'
      ? (block.commission_flat_fee || 0)
      : totalFee * block.commission_pct;
    const commPerItem = Math.round((totalCommission / deliverables.length) * 100) / 100;
    const commLastAdjust = Math.round((totalCommission - commPerItem * (deliverables.length - 1)) * 100) / 100;
    return deliverables.map((d, i) => ({
      ...d,
      fee: i === deliverables.length - 1 ? lastAdjust : perItem,
      commission: i === deliverables.length - 1 ? commLastAdjust : commPerItem,
    }));
  };

  const updateDeliverable = (index: number, field: keyof DeliverableData, value: any) => {
    const updated = [...block.deliverables];
    updated[index] = { ...updated[index], [field]: value };
    onChange({
      ...block,
      deliverables: updated
    });
  };

  const moveDeliverable = (index: number, direction: 'up' | 'down') => {
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= block.deliverables.length) return;
    const updated = [...block.deliverables];
    [updated[index], updated[newIndex]] = [updated[newIndex], updated[index]];
    onChange({
      ...block,
      deliverables: updated
    });
  };

  const removeDeliverable = (index: number) => {
    const remaining = block.deliverables.filter((_, i) => i !== index);
    const redistributed = redistributeFees(remaining, block.agreed_fee);
    onChange({ ...block, deliverables: redistributed });
  };

  // Green when everything is published, blue once work is out, amber in production
  const tone: RowTone = totalCount > 0 && publishedCount === totalCount
    ? 'success'
    : block.deliverables.some(d => ['Published', 'Approved', 'Submitted'].includes(d.status))
      ? 'info'
      : block.deliverables.some(d => d.status === 'In Production')
        ? 'warning'
        : 'neutral';

  return (
    <ExpandableRow
      tone={tone}
      isExpanded={isExpanded}
      onToggle={onToggleExpand}
      ariaLabel={`${block.influencer_name || t('form.campaign.newInfluencer')} - ${publishedCount}/${totalCount}`}
      dragHandle={dragHandleProps ? { ...dragHandleProps.attributes, ...dragHandleProps.listeners } as React.HTMLAttributes<HTMLButtonElement> : null}
      summary={<>
        <span className="text-sm font-semibold text-gray-900 truncate max-w-[260px]">
          {block.influencer_name || t('form.campaign.newInfluencer')}
        </span>
        <span className={`cm-chip ${publishedCount === totalCount && totalCount > 0 ? 'cm-chip-success' : 'cm-chip-neutral'}`}>
          {publishedCount}/{totalCount} {t('status.published').toLowerCase()}
        </span>
      </>}
      aside={block.agreed_fee > 0 ? new Intl.NumberFormat('pt-PT', { style: 'currency', currency: 'EUR' }).format(block.agreed_fee) : ''}
      actions={
          <div className="relative">
            <button
              type="button"
              onClick={() => setShowActions(!showActions)}
              className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              aria-label={t('common.actions')}
            >
              <MoreVertical className="w-4 h-4" />
            </button>
            {showActions && (
              <>
                <div
                  className="fixed inset-0 z-10"
                  onClick={() => setShowActions(false)}
                />
                <div className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg py-1 z-20 min-w-[150px]">
                  <button
                    type="button"
                    onClick={() => {
                      onDuplicate();
                      setShowActions(false);
                    }}
                    className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2"
                  >
                    <Copy className="w-4 h-4" />
                    {t('common.duplicate')}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      if (canRemove) {
                        onRemove();
                        setShowActions(false);
                      }
                    }}
                    disabled={!canRemove}
                    title={removeDisabledReason}
                    className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2 text-red-600 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Trash2 className="w-4 h-4" />
                    {t('common.remove')}
                  </button>
                </div>
              </>
            )}
          </div>
      }
    >
          {/* Main fields grid */}
          <div className="cm-grid grid grid-cols-1 md:grid-cols-2 gap-4 pt-4">
            {/* Influencer */}
            <div className="cm-field">
              <label className="cm-label">
                {t('form.campaign.influencer')} <span className="cm-req">*</span>
              </label>
              {block.existing ? (
                <div className="cm-readonly">{block.influencer_name}</div>
              ) : (
                <input
                  type="text"
                  list={`influencers-${block.id}`}
                  value={block.influencer_name}
                  onChange={(e) => handleInfluencerChange(e.target.value)}
                  required
                  placeholder={t('form.influencerPlaceholder')}
                  className="cm-input"
                />
              )}
              <datalist id={`influencers-${block.id}`}>
                {availableInfluencers.map((influencer) => (
                  <option key={influencer.id} value={influencer.full_name} />
                ))}
              </datalist>
            </div>

            {/* Agreed fee */}
            <div className="cm-field">
              <label className="cm-label">
                {t('form.campaign.fee')} <span className="cm-req">*</span>
              </label>
              <div className="cm-affix has-left">
                <span>€</span>
                <input
                  type="number"
                  value={block.agreed_fee || ''}
                  onChange={(e) => handleFieldChange('agreed_fee', parseFloat(e.target.value) || 0)}
                  step="0.01"
                  min="0"
                  required
                  className="cm-input cm-numeric"
                />
              </div>
            </div>

            {/* Commission type + value */}
            <div className="cm-field">
              <label className="cm-label">
                {t('form.campaign.commissionType')}
              </label>
              <div className="flex rounded-xl overflow-hidden border border-gray-300 mb-2">
                <button
                  type="button"
                  onClick={() => handleFieldChange('commission_type', 'percentage')}
                  className={`flex-1 px-3 py-1.5 text-xs font-medium transition-colors ${
                    block.commission_type === 'percentage'
                      ? 'bg-accent-500 text-white'
                      : 'bg-white text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  {t('form.commission.percentage')}
                </button>
                <button
                  type="button"
                  onClick={() => handleFieldChange('commission_type', 'flat_fee')}
                  className={`flex-1 px-3 py-1.5 text-xs font-medium transition-colors ${
                    block.commission_type === 'flat_fee'
                      ? 'bg-accent-500 text-white'
                      : 'bg-white text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  {t('form.commission.flatFee')}
                </button>
              </div>
              <label className="cm-label">
                {block.commission_type === 'percentage'
                  ? t('form.campaign.commissionPct')
                  : t('form.campaign.commissionAmount')} <span className="cm-req">*</span>
              </label>
              {block.commission_type === 'flat_fee' ? (
                <div className="cm-affix has-left">
                  <span>€</span>
                  <input
                    type="number"
                    value={block.commission_flat_fee || ''}
                    onChange={(e) => {
                      const value = parseFloat(e.target.value) || 0;
                      handleFieldChange('commission_flat_fee', value);
                    }}
                    step="0.01"
                    min="0"
                    required
                    className="cm-input cm-numeric"
                  />
                </div>
              ) : (
                <div className="cm-affix has-right">
                  <input
                    type="number"
                    value={Math.round(block.commission_pct * 100)}
                    onChange={(e) => {
                      const value = parseFloat(e.target.value) || 0;
                      handleFieldChange('commission_pct', value / 100);
                    }}
                    step="1"
                    min="0"
                    max="100"
                    required
                    className="cm-input cm-numeric"
                  />
                  <span>%</span>
                </div>
              )}
              <p className="cm-help">
                {block.commission_type === 'percentage'
                  ? t('form.commission.percentageHelp')
                  : t('form.commission.flatFeeHelp')}
              </p>
            </div>

            {/* Commission value (percentage mode) */}
            {block.commission_type === 'percentage' && (
              <div className="cm-field">
                <label className="cm-label">
                  {t('form.campaign.commissionValue')}
                </label>
                <div className="cm-readonly cm-readonly--accent">
                  {new Intl.NumberFormat('pt-PT', { style: 'currency', currency: 'EUR' }).format(
                    calculateCommissionAmount()
                  )}
                </div>
              </div>
            )}

            {/* Net fee */}
            <div className="cm-field">
              <label className="cm-label">
                {t('form.campaign.netFee')}
              </label>
              <div className="cm-readonly">
                {new Intl.NumberFormat('pt-PT', { style: 'currency', currency: 'EUR' }).format(
                  calculateNetPay()
                )}
              </div>
            </div>

            {/* Notes */}
            <div className="cm-field md:col-span-2">
              <label className="cm-label">
                {t('form.notes')}
              </label>
              <textarea
                value={block.notes}
                onChange={(e) => handleFieldChange('notes', e.target.value)}
                rows={2}
                placeholder={t('form.notesPlaceholder')}
                className="cm-input"
              />
            </div>
          </div>

          {/* Deliverables section */}
          <div className="border-t border-gray-200 pt-4">
            <div className="flex items-center justify-between mb-3">
              <h4 className="cm-block-title text-sm font-semibold text-gray-900">{t('form.deliverables')}</h4>
              <button
                type="button"
                onClick={addDeliverable}
                className="cm-btn-add"
              >
                <Plus className="w-4 h-4" />
                {t('form.addDeliverable')}
              </button>
            </div>

            {block.deliverables.length === 0 ? (
              <div className="text-sm text-gray-500 text-center py-4 bg-white border border-dashed border-gray-300 rounded-lg">
                {t('form.noDeliverables')}
              </div>
            ) : (
              <RowList>
                {block.deliverables.map((deliverable, index) => (
                  <DeliverableRow
                    key={deliverable.id}
                    index={index}
                    count={block.deliverables.length}
                    content={deliverable.content}
                    date={deliverable.date}
                    status={deliverable.status}
                    statusOptions={[
                      { value: 'Draft', label: t('status.draft') },
                      { value: 'In Production', label: t('status.production') },
                      { value: 'Submitted', label: t('status.submitted') },
                      { value: 'Approved', label: t('status.approved') },
                      { value: 'Published', label: t('status.published') },
                      { value: 'Rejected', label: t('status.rejected') },
                    ]}
                    contentPlaceholder="Conteúdo (ex: 1x Reel Instagram)"
                    onContent={(value) => updateDeliverable(index, 'content', value)}
                    onDate={(value) => updateDeliverable(index, 'date', value)}
                    onStatus={(value) => updateDeliverable(index, 'status', value as DeliverableStatus)}
                    onMove={(direction) => moveDeliverable(index, direction)}
                    onRemove={block.deliverables.length > 1 ? () => removeDeliverable(index) : undefined}
                    money={{
                      fee: deliverable.fee,
                      commission: deliverable.commission,
                      onFee: (value) => updateDeliverable(index, 'fee', value),
                      onCommission: (value) => updateDeliverable(index, 'commission', value),
                    }}
                    labels={{
                      moveUp: t('form.moveUp'),
                      moveDown: t('form.moveDown'),
                      remove: t('form.removeDeliverable'),
                      published: t('status.published'),
                      fee: 'Fee',
                      commission: t('form.campaign.commissionValue'),
                    }}
                  />
                ))}
              </RowList>
            )}
            {block.deliverables.length > 0 && (
              <div className="flex items-center justify-between mt-3 pt-2 border-t border-gray-100">
                <span className="text-xs text-gray-500">
                  Total: <span className="font-medium text-gray-700">{totalCount}</span> {totalCount === 1 ? t('form.deliverable') : t('form.deliverables').toLowerCase()}
                  <span className="mx-1.5 text-gray-300">|</span>
                  <span className="font-medium text-accent-600">{publishedCount}</span> {t('status.published').toLowerCase()}
                </span>
                <span className="text-xs tabular-nums text-gray-600">
                  Fees: <span className="font-medium">{new Intl.NumberFormat('pt-PT', { style: 'currency', currency: 'EUR' }).format(block.deliverables.reduce((s, d) => s + (d.fee || 0), 0))}</span>
                  <span className="mx-1.5 text-gray-300">|</span>
                  <span className="text-accent-600">Comissao: <span className="font-medium">{new Intl.NumberFormat('pt-PT', { style: 'currency', currency: 'EUR' }).format(block.deliverables.reduce((s, d) => s + (d.commission || 0), 0))}</span></span>
                </span>
              </div>
            )}
          </div>
    </ExpandableRow>
  );
}
